import { useState, type CSSProperties } from 'react';
import { ApiError, apiSendJson } from '../api-client.js';
import { useAuth } from '../auth.js';

interface Props {
  org: string;
  onClose: () => void;
  onSaved: () => void;
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: 'var(--loop-space-300)',
  zIndex: 200,
  overflowY: 'auto',
};

const dialogStyle: CSSProperties = {
  background: 'var(--loop-neutral-0)',
  borderRadius: 'var(--loop-radius-md)',
  padding: 'var(--loop-space-300)',
  width: '100%',
  maxWidth: 400,
  marginTop: 'var(--loop-space-300)',
  boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
};

export function OptInModal({ org, onClose, onSaved }: Props) {
  const { idToken } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appNotInstalled, setAppNotInstalled] = useState(false);

  async function doOptIn() {
    setSaving(true);
    setError(null);
    try {
      await apiSendJson(`/api/dv-config/org-optin/${encodeURIComponent(org)}`, 'POST', idToken!);
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.status === 400 && e.body.includes('app_not_installed')) {
        setAppNotInstalled(true);
      } else if (e instanceof ApiError) {
        setError(e.body);
      } else {
        setError((e as Error).message);
      }
    } finally {
      setSaving(false);
    }
  }

  const installUrl = `https://github.com/organizations/${encodeURIComponent(org)}/settings/installations`;

  return (
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={dialogStyle}>
        <h3 style={{ margin: '0 0 var(--loop-space-200)', fontSize: '1rem', fontWeight: 664, color: 'var(--loop-neutral-1100)' }}>
          Opt in {org}
        </h3>

        {appNotInstalled ? (
          <div style={{ marginBottom: 'var(--loop-space-200)', fontSize: '0.875rem', color: 'var(--loop-neutral-900)', lineHeight: 1.5 }}>
            <em>
              The Diff Vader GitHub App isn&apos;t installed on <strong>{org}</strong>.{' '}
              <a
                href={installUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--loop-blue-600)' }}
              >
                Install it
              </a>
              , then click Retry.
            </em>
          </div>
        ) : (
          <p style={{ fontSize: '0.875rem', color: 'var(--loop-neutral-900)', marginBottom: 'var(--loop-space-200)' }}>
            Enable Diff Vader for <strong>{org}</strong>?
          </p>
        )}

        {error && (
          <div style={{ color: 'var(--loop-red-700)', fontSize: '0.8rem', marginBottom: 'var(--loop-space-150)', padding: 'var(--loop-space-100)', background: 'var(--loop-red-100)', borderRadius: 'var(--loop-radius-xs)' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--loop-space-100)', justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn-primary" onClick={() => { void doOptIn(); }} disabled={saving}>
            {saving ? 'Working…' : appNotInstalled ? 'Retry' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
